'use strict';
/* ==========================================================================
   levels/name.js — step "name" (orientation any): NAME THAT DEVICE (DESIGN.md §5.2, look §2).

   A photocopied zine page taped to the corkboard. An attempt = 8 lines from content/bank.name.json, drawn by the model
   (Devices.attempt('name', json): seeded, every draw.require tag covered, easiest first, the right answers spread over
   the slots). Nothing here decides which device a line uses: Devices.check(itemId, deviceId) does (an id in the
   example's `accept` list counts as right: Sandburg's fog as personification, Marley's door-nail as a simile). An
   item's accepted readings that aren't among its content options are added as chips (so 3–4 chips), at a seeded slot.

   Per line:  the line types on (public-domain quotes show author, work and year in small print; ours show nothing)
           →  device: "1 · WHICH DEVICE?" the options as highlighter chips (name + doodle). A wrong chip is crossed out
              and the margin gets the item's `why` + that device's one-line definition (Devices.DEVICES[id].short);
              pick again. Right: the words that make the device are highlighted in its colour (SPANS below; the
              whole line when no span is known) and the margin names it with its definition.
           →  effect: "2 · WHAT DOES IT DO HERE?" the item's 3 effects (shuffled by the model; content index 0 is
              right); a wrong one is crossed out, pick again
           →  note: Ms. Park's margin note (the stars for this line), "Next line ▶".
   Stars: right device first try 1 + right effect first try 1; of 16, pass = bank.name.json passStars (11).
   Game.progress.scores.name = {best, last, of: 16}; first answers under Game.progress.answers['name/<id>/device'] and
   ['name/<id>/effect']. A pass → "Collect your stamp" (Game.complete('name')); leaving a passed attempt without
   collecting still keeps the stamp. The end card lists the 8 lines colour-coded by device.
   Attempt + seed: Game.progress.bank.name = {pool, seed, attempt, ids, prev, open, run: {i, ev, res}}: a reload
   mid-attempt resumes the same line with the same picks (?seed= / window.__bankSeed force a seed).

   Portrait phone (Dock.active): the page (the line, big) is the picture on the stage; the chips, the effects, the
   margin notes and the buttons are dock widgets.
   Keys: 1–4 = chip / effect, Enter / Space = start / next line.
   SFX: 'typewriter' (a line types on) and 'snaps' (right) when the lesson has them, else shared 'paper' / 'ding'.

   QA hooks: sc.phase ('intro' | 'device' | 'effect' | 'note' | 'end'), sc.line (index 0–7), sc.item (the drawn
   instance), sc.items, sc.seed, sc.attempt, sc.resumed, sc.results, sc.stars, sc.picks, sc.answer() / sc.solution
   ({device, effect}: the right option indexes of the current step's lists), sc.choose(i), sc.next(), sc.begin(fresh),
   sc.collect(), sc.done(); Levels.name.solution (the live scene's), Levels.name.chips() (the chip / effect tap rects),
   Levels.name.preview(id) (show that item as the current line: screenshots of the longest quotes),
   Levels.name.SPANS. Integration compat: sc.preview ({items}) and sc.finishStub(stars = 16) (records that score and
   completes the step).
   ========================================================================== */
(function () {
  const STEP = 'name', TITLE = 'NAME THAT DEVICE', OF_ITEM = 2, NEXT_GUARD = 0.3, CPS = 40;
  const Z = () => window.Zine;
  const json = () => Game.content.bank_name || { items: [], draw: {} };
  const HAND = "'Caveat Brush'", BLUE = '#2b4a8c';
  const PG = { x: 900, y: 592, w: 1480, h: 920 };            // the page (desktop / landscape)
  const PGD = { x: 960, y: 548, w: 1840, h: 1040 };           // the page (portrait: the stage is only the page)
  const COL = { x: 232, w: 980, mx: 1266, mw: 336 };          // text column + margin column (desktop)

  /* the words that make each line's device (substrings of the item's text). A span that isn't found (the content
     changed) falls back to highlighting the whole line; tools/qa_name.js checks every span is found. */
  const SPANS = {
    'bus-turtle': ['bus', 'as slow as a sleepy turtle'],
    'brother-tornado': ['little brother', 'a tornado'],
    'alarm-screamed': ['alarm clock screamed'],
    'million-times': ['a million times'],
    'seagulls': ['Seven silly seagulls sang', 'sand'],
    'buzz-crack': ['Buzz', 'Crack', 'Splash'],
    'clock-tick': ['Tick, tock'],
    'spill-beans': ['spill the beans'],
    'no-einstein': ['Einstein'],
    'pop-quiz': ['Oh great', 'My favorite.'],
    'longer-day': ['Who wants a longer school day? Anyone?'],
    'burns-rose': ['luve\'s like a red, red rose'],
    'burns-seas': ['Till a\' the seas gang dry', 'the rocks melt wi\' the sun'],
    'sandburg-fog': ['fog', 'little cat feet'],
    'dickinson-death': ['Death', 'He kindly stopped for me'],
    'world-stage': ['All the world\'s a stage', 'merely players'],
    'lonely-cloud': ['I wandered lonely as a Cloud'],
    'henry-chains': ['Is life so dear, or peace so sweet, as to be purchased at the price of chains and slavery?'],
    'marley-doornail': ['as dead as a door-nail'],
    'hughes-stair': ['Life for me ain\'t been no crystal stair'],
    'envious-moon': ['envious moon', 'sick and pale with grief'],
    'wind-whispered': ['wind whispered'],
  };
  function ranges(it) {
    const out = [];
    for (const s of SPANS[it.id] || []) { const a = it.text.indexOf(s); if (a >= 0) out.push([a, a + s.length]); }
    return out.length ? out : [[0, it.text.length]];
  }

  /* the source in small print: "— Author, "Work", year" (the year only when author/work don't carry it) */
  function sourceText(src) {
    if (!src || typeof src !== 'object') return '';
    const work = /^[A-Z]/.test(src.work || '') ? `"${src.work}"` : (src.work || '');
    const hasYear = String(src.author || '').includes(String(src.year)) || String(src.work || '').includes(String(src.year));
    return `— ${src.author}${work ? ', ' + work : ''}${hasYear ? '' : ', ' + src.year}`;
  }

  /* break a line into typed lines: " / " starts a new verse line; each keeps its start offset in the text (highlights) */
  function breakLines(text, maxW, size) {
    const out = [], Zn = Z();
    let off = 0;
    text.split(' / ').forEach(v => {
      let line = '', ls = off, p = off;
      for (const w of v.split(' ')) {
        const t = line ? line + ' ' + w : w;
        if (line && Zn.measure(t, size) > maxW) { out.push({ str: line, start: ls }); line = w; ls = p; }
        else { if (!line) ls = p; line = t; }
        p += w.length + 1;
      }
      if (line) out.push({ str: line, start: ls });
      off += v.length + 3;
    });
    return out;
  }

  /* chips flowed into rows inside width W */
  function chipRow(ids, x0, W, top, cs, ch, gap) {
    const Zn = Z(), rows = [[]];
    let x = 0;
    ids.forEach((id, i) => {
      const w = Math.round(Zn.measure(Devices.name(id).toUpperCase(), cs) + cs * 2.6);
      if (rows[rows.length - 1].length && x + w > W) { rows.push([]); x = 0; }
      rows[rows.length - 1].push({ i, id, w, rx: x }); x += w + gap;
    });
    const out = [];
    rows.forEach((r, k) => r.forEach(c => out.push({ i: c.i, id: c.id, w: c.w, h: ch, x: x0 + c.rx + c.w / 2, y: top + k * (ch + 18) + ch / 2 })));
    return { chips: out.sort((a, b) => a.i - b.i), h: rows.length * ch + (rows.length - 1) * 18 };
  }

  /* the page layout of one line (desktop / landscape, or the portrait stage). Cached per line + UI scale + fonts. */
  function layout(it, docked) {
    const Zn = Z();
    const P0 = docked ? PGD : PG, top = P0.y - P0.h / 2, bottom = P0.y + P0.h / 2 - 22;
    const TX = docked ? P0.x - P0.w / 2 + 90 : COL.x, TW = docked ? P0.w - 180 : COL.w;
    const hs = docked ? 40 : Math.round(UI.text(28));
    const qs = Math.round(UI.text(34)), cs = Math.round(UI.text(34)), os = Math.round(UI.text(30));
    const ch = Math.max(UI.tap(84), Math.round(cs * 1.5) + 14);
    const opts = it.options.map(o => o.id), effs = it.effect.options;
    const chipW = Math.max(...opts.map(id => Zn.measure(Devices.name(id).toUpperCase(), cs) + cs * 2.6));
    let L;
    /* first try: the named chip on its own "1 · WHICH DEVICE?" line; compact (a tight page, e.g. a phone sideways):
       the named chip moves up into the header row */
    for (const compact of docked ? [false] : [false, true]) {
      let fits = false;
      for (let k = 0; k < 14; k++) {
        const size = docked ? Math.max(60, 124 - k * 6) : Math.max(34, 58 - k * 2);
        const srcS = docked ? 38 : Math.min(Math.round(UI.text(26)), Math.round(size * 0.66));   // small print never outgrows the line
        const lh = Math.round(size * 1.34), lines = breakLines(it.text, TW, size);
        const headY = top + (docked ? 70 : compact ? Math.max(58, ch / 2 + 12) : 58), lineTop = headY + (docked ? 60 : compact ? ch / 2 + 22 : 46);
        lines.forEach((l, j) => { l.y = lineTop + (j + 0.5) * lh; });
        const src = sourceText(it.source), srcL = src ? UI._wrap(src, TW, `${srcS}px ${Zn.FONT}`) : [];
        const srcTop = lineTop + lines.length * lh + 8, srcLh = srcS * 1.3;
        const qTop = srcTop + srcL.length * srcLh + (docked ? 40 : 30);
        L = { k, compact, top, bottom, TX, TW, hs, headY, size, lh, lines, src: srcL, srcS, srcTop, srcLh, qTop, qs, cs, ch, os, P0 };
        if (docked) { L.chipY = qTop + 50; if (L.chipY + 60 <= bottom) { fits = true; break; } continue; }
        // step 1: the question + the chips
        const q1Y = qTop + qs * 0.7, cr = chipRow(opts, TX, TW, q1Y + qs * 0.7 + 14, cs, ch, 26);
        const needDevice = q1Y + qs * 0.7 + 14 + cr.h;
        // step 2: the answered chip (on the question line, or in the header when compact), the question, the effects
        const q2Y = compact ? qTop + qs * 0.7 : qTop + ch + 16 + qs * 0.7;
        let y = q2Y + qs * 0.7 + 14;
        const rows = effs.map((e, i) => {
          const tl = UI._wrap(e, TW - 110, `${os}px ${Zn.FONT}`), h = Math.max(UI.tap(80), Math.round(tl.length * os * 1.3 + 26));
          const r = { i, lines: tl, x: TX + TW / 2, y: y + h / 2, w: TW, h }; y += h + 12; return r;
        });
        const chipFits = compact ? Zn.measure(`LINE 8 OF 8`, hs) + 40 + chipW <= TW : Zn.measure('1 · WHICH DEVICE?', qs) + 40 + chipW <= TW;
        Object.assign(L, { q1Y, chips: cr.chips, q2Y, rows });
        if (Math.max(needDevice, y) <= bottom && chipFits) { fits = true; break; }
      }
      if (fits) break;
    }
    L.ranges = ranges(it);
    return L;
  }

  class NameScene extends Scene {
    get n() { return this.items ? this.items.length : (json().draw && json().draw.n) || 8; }
    get of() { return this.n * OF_ITEM; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get item() { return this.items ? this.items[this.line] : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { const it = this.item; return it ? { device: it.answerIndex, effect: it.effect.answerIndex } : null; }
    answer() { return this.solution; }
    /* integration compat: the attempt's draw (or a peek at one before Start) */
    get preview() {
      if (this.items) return { seed: this.seed, items: this.items };
      if (!this._peek) { const f = Bank.seedOverride(); this._peek = Devices.drawName(json(), { seed: f != null ? f : 1 }); }
      return this._peek;
    }

    enter() {
      this.phase = 'intro'; this.items = null; this.line = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map(); this.quiet = false;
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Devices.attempt: seeded, saved in Game.progress.bank.name) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const P_ = Game.progress, S = P_.bank || (P_.bank = {}), st = S[STEP];
      const run = !fresh && st && st.open && st.pool === STEP ? st.run : null;
      const a = Devices.attempt(STEP, json(), { fresh });
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.items = a.items.map((it, k) => withAccept(it, a.seed, k));
      this.results = []; this.passed = false; this.collected = false; this.lays.clear();
      if (a.resumed && run && this.restore(run)) return;
      this.startLine(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.items.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.lineAt = Game.time - 5; this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 'c') this.choose(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[name] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.line = i; this.phase = 'device';
      this.ev = []; this.picks = []; this.epicks = []; this.lastWrong = null;
      this.lineAt = Game.time; this.rightAt = this.effAt = this.noteAt = this.pickedAt = null; this.accepted = null; this.rightId = null;
    }
    startLine(i) {
      this.setup(i);
      this.save();
      if (!this.quiet) { if (Audio.has('typewriter')) Audio.play('typewriter', { gain: 0.45 }); else Audio.play('paper', { gain: 0.4 }); }
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.line, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, alt, o) { if (this.quiet) return; const n = Audio.has(name) ? name : alt; if (n) Audio.play(n, o); }

    /* ---- pick a chip (device phase) or an effect (effect phase) ---- */
    choose(i) {
      const it = this.item;
      if (!it) return false;
      if (this.phase === 'device') {
        if (!(i >= 0 && i < it.options.length) || this.picks.includes(i)) return false;
        const id = it.options[i].id, c = Devices.check(it.id, id);
        this.ev.push(['c', i]);
        if (!this.picks.length) Game.progress.answer(`${STEP}/${it.id}/device`, id);   // first try only
        this.picks.push(i); this.pickedAt = Game.time;
        if (c.ok) {
          this.rightId = id; this.accepted = c.accepted; this.rightAt = Game.time; this.effAt = Game.time + 0.6;
          this.phase = 'effect'; this.lastWrong = null;
          this.sfx('snaps', 'ding', { gain: 0.6 });
        } else { this.lastWrong = i; this.sfx('thunk', null, { gain: 0.4, rate: 0.9 }); }
        this.save();
        return true;
      }
      if (this.phase === 'effect') {
        const q = it.effect;
        if (!(i >= 0 && i < q.options.length) || this.epicks.includes(i)) return false;
        this.ev.push(['c', i]);
        if (!this.epicks.length) Game.progress.answer(`${STEP}/${it.id}/effect`, q.options[i]);
        this.epicks.push(i); this.pickedAt = Game.time;
        if (i === q.answerIndex) {
          const ds = this.picks.length === 1 ? 1 : 0, es = this.epicks.length === 1 ? 1 : 0;
          this.results[this.line] = { line: this.line, id: it.id, device: this.rightId, main: it.device, accepted: this.accepted,
            wrongDevices: this.picks.length - 1, wrongEffects: this.epicks.length - 1, deviceStar: ds, effectStar: es, stars: ds + es };
          this.phase = 'note'; this.noteAt = Game.time;
          this.sfx(ds + es === 2 ? 'snaps' : 'ding', 'ding', { gain: 0.6 });
        } else this.sfx('thunk', null, { gain: 0.35, rate: 0.8 });
        this.save();
        return true;
      }
      return false;
    }
    next() {
      if (this.phase !== 'note' || Game.time - this.noteAt < NEXT_GUARD) return false;
      Audio.play('paper', { gain: 0.5 });
      if (this.line < this.items.length - 1) this.startLine(this.line + 1);
      else this.finish();
      return true;
    }
    finish(stars = this.stars) {
      this.phase = 'end'; this.endAt = Game.time; this.passed = stars >= this.passStars; this.finalStars = stars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Devices.finish(STEP);                                                       // the next attempt draws new lines
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, stars), last: stars, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.items) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    /* integration compat: record `stars` (default all 16) and complete the step */
    finishStub(stars = this.of) {
      if (this.collected) return;
      if (!this.items) this.begin();
      if (this.phase !== 'end') this.finish(stars);
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new lines)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new lines)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'device' || this.phase === 'effect') { if (n >= 1 && n <= 4) this.choose(n - 1); }
      else if (this.phase === 'note') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const key = this.line + '|' + this.item.id + '|' + (docked ? 'd' : 'w') + '|' + Math.round(UI.text(30)) + '|' + Math.round(UI.tap(84)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { if (this.lays.size > 12) this.lays.clear(); L = layout(this.item, docked); this.lays.set(key, L); }
      return L;
    }
    draw() {
      const docked = UI.docked(), Zn = Z();
      Zn.cork();
      UI.hud(TITLE, '');
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      this.drawPage(L, docked);
      if (docked) return this.drawDock();
      this.drawPark(1792, 1080, 520);
      if (this.phase === 'device') this.drawChips(L);
      else this.drawEffect(L);
      this.drawMargin(L);
    }
    drawPark(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('park', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0 });
    }

    /* the page: header, the line typed on (highlighted once named), its source in small print */
    drawPage(L, docked) {
      const Zn = Z(), it = this.item, P0 = L.P0, t0 = this.lineAt;
      const k = E.outCubic(clamp((T - t0) / 0.35)), slide = (1 - k) * 60;
      g.save(); g.translate(slide, 0);
      Zn.page(P0.x, P0.y, P0.w, P0.h, { seed: 211 + this.line * 7, rot: -0.004 + (this.line % 3) * 0.003, tape: 1, dark: 0.45 });
      // header: "LINE 3 OF 8" and the stars so far
      Zn.type(`LINE ${this.line + 1} OF ${this.n}`, L.TX, L.headY, L.hs, { seed: 5, color: Zn.C.grey });
      if (!(L.compact && this.rightId)) Zn.type(`★ ${this.stars}`, L.TX + L.TW, L.headY, L.hs, { seed: 6, color: Zn.C.grey, align: 'right' });
      if (!docked) {   // the margin rule (a red ballpoint line)
        ink([[COL.mx - 26, L.top + 34], [COL.mx - 28, L.bottom - 10]], { w: 2.5, color: 'rgba(217,53,44,0.55)', seed: 17, passes: 1, step: 40, amp: 0.8 });
      }
      // the line, typed on; the right answer's highlight under the words that make the device
      let before = 0;
      const typeAt = t0 + 0.3;
      L.lines.forEach((l, j) => {
        if (this.rightAt != null) {
          L.ranges.forEach(([a, b], r) => {
            const s = Math.max(a, l.start), e = Math.min(b, l.start + l.str.length);
            if (s >= e) return;
            const x0 = L.TX + Zn.measure(l.str.slice(0, s - l.start), L.size), x1 = L.TX + Zn.measure(l.str.slice(0, e - l.start), L.size);
            Zn.highlight(x0 - 6, l.y, x1 - x0 + 12, L.size * 1.08, Devices.colour(this.rightId), { t0: this.rightAt + 0.1 + (j + r) * 0.12, seed: 40 + j * 5 + r });
          });
        }
        Zn.type(l.str, L.TX, l.y, L.size, { t0: typeAt + before / CPS, cps: CPS, seed: 10 + j + this.line * 13, cursor: j === L.lines.length - 1 });
        before += l.str.length + 1;
      });
      const srcAt = typeAt + before / CPS;
      L.src.forEach((s, j) => Zn.type(s, L.TX + 4, L.srcTop + (j + 0.5) * L.srcLh, L.srcS, { t0: srcAt + j * 0.2, cps: 90, color: Zn.C.grey, seed: 30 + j }));
      if (docked && this.rightId) Zn.label(this.rightId, L.TX + Zn.measure(Devices.name(this.rightId).toUpperCase(), 44) / 2 + 60, L.chipY, { size: 44, rot: -0.02, s: pop(T, this.rightAt, 0.3), seed: 60 });
      g.restore();
    }

    /* step 1: "WHICH DEVICE?" + the chips */
    drawChips(L) {
      const Zn = Z(), it = this.item, t0 = this.lineAt + 0.5;
      Zn.type('1 · WHICH DEVICE?', L.TX, L.q1Y, L.qs, { t0, cps: 45, seed: 50, color: BLUE });
      L.chips.forEach(c => {
        const s = pop(T, t0 + 0.25 + c.i * 0.08, 0.35), crossed = this.picks.includes(c.i);
        if (s <= 0) return;
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('nm-chip' + this.line + '_' + c.i, c.x, c.y, c.w + 12, c.h);
        const rot = [-0.02, 0.015, -0.01, 0.02][c.i % 4];
        g.save(); if (crossed) g.globalAlpha *= 0.42;
        Zn.label(c.id, c.x, c.y + (hit.hover ? -5 : 0), { size: L.cs, rot, s: s * (hit.hover ? 1.05 : 1), seed: 70 + c.i });
        g.restore();
        if (crossed) {
          const a = c.i === this.lastWrong ? this.pickedAt : -1e9;
          ink([[c.x - c.w / 2 - 6, c.y + 12], [c.x + c.w / 2 + 6, c.y - 12]], { w: 6, color: Zn.C.red, seed: 80 + c.i, prog: clamp((T - a) / 0.25), passes: 1 });
        }
        if (UI.keys && !crossed) this.keyBadge(String(c.i + 1), c.x - c.w / 2 + 4, c.y - L.cs * 0.75);
        if (hit.clicked) this.choose(c.i);
      });
    }
    /* step 2: the named chip on the question line, "WHAT DOES IT DO HERE?" + the effect rows */
    drawEffect(L) {
      const Zn = Z(), it = this.item, q = it.effect, t0 = this.effAt, note = this.phase === 'note';
      const cw = Zn.measure(Devices.name(this.rightId).toUpperCase(), L.cs) + L.cs * 2.6;
      if (L.compact) Zn.label(this.rightId, L.TX + Zn.measure(`LINE ${this.line + 1} OF ${this.n}`, L.hs) + 40 + cw / 2, L.headY, { size: L.cs, rot: -0.015, s: pop(T, this.rightAt, 0.3), seed: 90 });
      else {
        const w1 = Zn.type('1 · WHICH DEVICE?', L.TX, L.qTop + L.ch / 2, L.qs, { seed: 50, color: BLUE });
        Zn.label(this.rightId, L.TX + w1 + 40 + cw / 2, L.qTop + L.ch / 2, { size: L.cs, rot: -0.015, s: pop(T, this.rightAt, 0.3), seed: 90 });
      }
      if (T < t0) return;
      Zn.type('2 · WHAT DOES IT DO HERE?', L.TX, L.q2Y, L.qs, { t0, cps: 45, seed: 51, color: BLUE });
      L.rows.forEach(r => {
        const s = pop(T, t0 + 0.3 + r.i * 0.08, 0.3, 1.6), crossed = this.epicks.includes(r.i) && r.i !== q.answerIndex, right = note && r.i === q.answerIndex;
        if (s <= 0) return;
        const hit = crossed || note ? { hover: false, clicked: false } : UI._hit('nm-eff' + this.line + '_' + r.i, r.x, r.y, r.w, r.h - 4);
        g.save(); g.translate(r.x, r.y); g.scale(s, s);
        paperRect(0, 0, Math.round(r.w / 10) * 10, Math.round((r.h - 8) / 2) * 2, hit.hover ? '#fffbe0' : crossed ? '#e4e1d8' : note && !right ? '#efede6' : Zn.C.white, { seed: 120 + r.i, depth: hit.hover ? 1.2 : 0.6, boil: false });
        g.restore();
        if (right) Zn.mark(this.rightId, r.x - r.w / 2 + 90, r.y, r.w - 120, r.h - 26, { t0: this.noteAt, seed: 130 + r.i, alpha: 0.55 });
        // the number block, then the effect typed
        g.save(); g.fillStyle = crossed ? Zn.C.grey : Zn.C.ink; g.fillRect(r.x - r.w / 2 + 22, r.y - 22, 44, 44);
        g.font = `28px ${Zn.FONT}`; g.fillStyle = Zn.C.white; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(r.i + 1), r.x - r.w / 2 + 44, r.y + 2); g.restore();
        const ty = r.y - (r.lines.length - 1) * L.os * 1.3 / 2;
        r.lines.forEach((l, j) => Zn.type(l, r.x - r.w / 2 + 90, ty + j * L.os * 1.3, L.os, { seed: 140 + r.i * 5 + j, color: crossed || (note && !right) ? Zn.C.grey : Zn.C.ink }));
        if (crossed) {
          const mw = Math.max(...r.lines.map(l => Zn.measure(l, L.os))), a = r.i === this.epicks[this.epicks.length - 1] && !note ? this.pickedAt : -1e9;
          ink([[r.x - r.w / 2 + 80, r.y + 10], [r.x - r.w / 2 + 100 + mw, r.y - 10]], { w: 5, color: Zn.C.red, seed: 150 + r.i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (right) inkTick(r.x + r.w / 2 - 50, r.y, 1, Zn.C.red, this.noteAt + 0.2);
        if (hit.clicked) this.choose(r.i);
      });
    }
    keyBadge(txt, x, y) {
      g.save(); g.fillStyle = Z().C.ink; g.beginPath(); g.arc(x, y, 17, 0, TAU); g.fill();
      g.font = `22px ${Z().FONT}`; g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, x, y + 1); g.restore();
    }

    /* the margin: what a wrong chip is + why not; the named device's definition; Ms. Park's note + Next line ▶ */
    marginNotes() {
      const it = this.item, out = [];
      if (this.phase === 'device') {
        if (this.lastWrong != null) {
          const id = it.options[this.lastWrong].id, D = Devices.DEVICES[id];
          const why = it.why[this.lastWrong] || '';
          out.push(['red', `Not ${Devices.name(id).toLowerCase()}.`, this.pickedAt]);
          if (why) out.push(['red', why, this.pickedAt + 0.2]);
          if (D) out.push(['grey', `${D.name}: ${D.short}`, this.pickedAt + 0.4]);
        }
        return out;
      }
      const D = Devices.DEVICES[this.rightId];
      out.push(['head', `${D.name}!`, this.rightAt + 0.2]);
      out.push(['blue', D.short, this.rightAt + 0.4]);
      if (this.accepted) out.push(['blue', `Counts! Readers also call this line ${aOrAn(Devices.name(it.device).toLowerCase())}.`, this.rightAt + 0.6]);
      if (this.phase === 'note') {
        const r = this.results[this.line] || { stars: 0 };
        out.push(['stars', r.stars === 2 ? '+2 ★★  Snaps!' : r.stars === 1 ? '+1 ★  Close.' : '+0  Next one.', this.noteAt + 0.2]);
        out.push(['sign', '— Ms. P.', this.noteAt + 0.5]);
      }
      return out;
    }
    drawMargin(L) {
      const x = COL.mx, w = COL.mw, notes = this.marginNotes();
      const fs = Math.round(UI.text(32)), lh = fs * 1.12;
      const col = { red: Z().C.red, grey: '#55524c', head: BLUE, blue: BLUE, stars: Z().C.red, sign: BLUE };
      let y = L.headY + 40;
      notes.forEach(([kind, text, at], n) => {
        const size = kind === 'head' || kind === 'stars' ? Math.round(fs * 1.2) : fs;
        const lines = UI._wrap(text, w - 10, `${size}px ${HAND}`);
        lines.forEach((l, j) => hand(l, kind === 'sign' ? x + w - 20 : x, y + (j + 0.5) * size * 1.12, size, at + j * 0.2, { align: kind === 'sign' ? 'right' : 'left', color: col[kind], seed: 200 + n * 7 + j, rot: -0.02 }));
        y += lines.length * size * 1.12 + 14;
      });
      if (this.phase === 'note') {
        const bh = UI.tap(96), by = Math.min(L.bottom - bh / 2 - 6, Math.max(y + 20 + bh / 2, 820));
        const label = this.line < this.n - 1 ? 'Next line ▶' : 'See my stars ▶';
        if (Game.time - this.noteAt > NEXT_GUARD && UI.button('nm-next', label, x + w / 2 - 6, by, { w: w + 10, h: 96, color: Z().C.red, size: 42 })) this.next();
      }
    }

    /* ---- portrait: the page is the picture; everything to tap is in the dock ---- */
    drawDock() {
      const it = this.item, id = this.attempt + '_' + this.line, names = it.options.map(o => o.name);
      Dock.text('nm-line' + id, [it.text.split(' / ').join(' / ')].concat(sourceText(it.source) ? [sourceText(it.source)] : []), { cls: 'note' });
      if (this.phase === 'device') {
        const st = it.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        const notes = it.options.map((o, i) => { if (!this.picks.includes(i)) return ''; const D = Devices.DEVICES[o.id]; return [it.why[i], D ? `${D.name}: ${D.short}` : ''].filter(Boolean).join(' '); });
        Dock.choices('nm-dev' + id, { prompt: '1 · Which device?', options: names, state: st, notes, onChoose: i => this.choose(i) });
        return;
      }
      Dock.choices('nm-dev' + id, { prompt: '1 · Which device?', options: names, state: it.options.map((o, i) => (o.id === this.rightId ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      const D = Devices.DEVICES[this.rightId];
      Dock.text('nm-def' + id, [`${D.name}: ${D.short}`].concat(this.accepted ? [`Counts! Readers also call this line ${aOrAn(Devices.name(it.device).toLowerCase())}.`] : []), { cls: 'right', reveal: true });
      const q = it.effect, note = this.phase === 'note';
      const st = q.options.map((o, i) => (note ? (i === q.answerIndex ? 'right' : this.epicks.includes(i) ? 'wrong' : 'faded') : this.epicks.includes(i) ? 'wrong' : ''));
      Dock.choices('nm-eff' + id, { prompt: '2 · What does it do here?', options: q.options, state: st, locked: note, onChoose: i => this.choose(i) });
      if (note) {
        const r = this.results[this.line] || { stars: 0 };
        Dock.text('nm-park' + id, [`Ms. Park: ${r.stars === 2 ? '+2 stars. Snaps!' : r.stars === 1 ? '+1 star. Close.' : 'No stars on this one. Next one.'}`], { cls: 'note', reveal: true });
        Dock.button({ id: 'next', glyph: this.line < this.n - 1 ? 'Next line ▶' : 'See my stars ▶', label: 'next', aria: 'next line', go: true, onClick: () => this.next() });
      }
    }

    introLines() {
      return ['Eight lines: some ours, some from famous writers.', '1. Which device is it? Tap its highlighter.',
        '2. What does it do here? Pick what it does to the meaning.', `A star for each right first try: ${this.passStars} of ${this.of} to publish.`];
    }
    drawIntro(docked) {
      const Zn = Z(), t0 = this.enteredAt, lines = this.introLines();
      if (docked) {
        Zn.page(960, 548, 1500, 900, { seed: 311, rot: -0.01, tape: 1 });
        Zn.type('NAME THAT DEVICE', 960, 380, 96, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 5 });
        Zn.highlight(420, 390, 1080, 96, '#fff04a', { t0: t0 + 0.7, seed: 7 });
        ['simile', 'metaphor', 'alliteration', 'irony'].forEach((id, i) => Zn.label(id, 960 + (i - 1.5) * 330, 640, { size: 40, rot: [-0.03, 0.02, -0.02, 0.03][i], s: pop(T, t0 + 0.8 + i * 0.1, 0.35), seed: 40 + i }));
        Dock.heading('nm-intro', 'Name That Device', { cls: 'big' });
        Dock.text('nm-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start naming devices', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      this.drawPark(1640, 1080, 760);
      Zn.page(800, 590, 1260, 880, { seed: 311, rot: -0.008, tape: 1 });
      Zn.type('NAME THAT DEVICE', 800, 250, 76, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 5 });
      Zn.highlight(800 - 380, 258, 760, 70, '#fff04a', { t0: t0 + 0.7, seed: 7 });
      const fs = Math.round(UI.text(36));
      let y = 360;
      lines.forEach((l, i) => UI._wrap(l, 1020, `${fs}px ${Zn.FONT}`).forEach(tl => {
        Zn.type(tl, 800 - 510, y, fs, { t0: t0 + 0.5 + i * 0.35, cps: 70, seed: 20 + i, color: i === 3 ? Zn.C.red : Zn.C.ink });
        y += fs * 1.5;
      }));
      ['simile', 'metaphor', 'alliteration', 'irony'].forEach((id, i) => Zn.label(id, 800 + (i - 1.5) * 290, y + 50, { size: 32, rot: [-0.03, 0.02, -0.02, 0.03][i], s: pop(T, t0 + 1.6 + i * 0.1, 0.35), seed: 40 + i }));
      if (this.t > 0.8 && UI.button('nm-start', 'Start ▶', 800, Math.min(960, y + 190), { w: 420, h: 110, color: Zn.C.red, size: 56 })) this.begin();
    }
    drawEnd(docked) {
      const Zn = Z(), s = this.finalStars != null ? this.finalStars : this.stars, of = this.of, btns = this.endButtons();
      const rows = this.items.map((it, i) => { const r = this.results[i] || { stars: 0, device: it.device }; return { it, r, dev: r.device || it.device }; });
      if (docked) {
        Zn.page(960, 548, 1500, 900, { seed: 411, rot: -0.01, tape: 1 });
        Zn.type(`${s} / ${of}`, 960, 420, 150, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 950 });
        Zn.stamp(this.passed ? 'PUBLISHED' : 'TRY AGAIN', 960, 700, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.4 });
        Dock.heading('nm-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('nm-end', [this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars to publish. New lines?`]);
        Dock.list('nm-end', rows.map((x, i) => ({ badge: String(i + 1), color: Devices.colour(x.dev), lines: [x.it.text.split(' / ').join(' / '), `${Devices.name(x.dev)} · ${x.r.stars} of 2 stars`] })));
        Dock.text('nm-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('nm-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      // the page: the 8 lines, each on its device's highlighter with the doodle and the stars
      Zn.page(760, 590, 1380, 920, { seed: 411, rot: -0.006, tape: 1 });
      Zn.type('THIS ISSUE', 130 + 60, 190, 44, { t0: this.endAt, cps: 40, seed: 900, color: BLUE });
      const fs = Math.round(UI.text(28)), top = 240, rh = Math.min(96, (1020 - top) / rows.length), X0 = 150, W = 1220;
      rows.forEach((x, i) => {
        const y = top + (i + 0.5) * rh, at = this.endAt + 0.3 + i * 0.1;
        Zn.mark(x.dev, X0 + 20, y, W - 320, rh * 0.62, { t0: at, seed: 910 + i, alpha: 0.6 });
        Zn.doodle(x.dev, X0 + 56, y, fs * 0.55, { t0: at, seed: 920 + i });
        let t = x.it.text.split(' / ').join(' / ');
        const maxW = W - 480;
        if (Zn.measure(t, fs) > maxW) { while (Zn.measure(t + '…', fs) > maxW && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        Zn.type(t, X0 + 100, y - fs * 0.32, fs, { seed: 930 + i });
        Zn.type(Devices.name(x.dev).toUpperCase(), X0 + 100, y + fs * 0.62, Math.round(fs * 0.72), { seed: 940 + i, color: Zn.C.grey });
        hand(x.r.stars === 2 ? '★★' : x.r.stars === 1 ? '★' : '–', X0 + W - 120, y, Math.round(fs * 1.4), at + 0.2, { color: x.r.stars === 2 ? BLUE : Zn.C.red, seed: 960 + i });
      });
      // the score, the stamp, the buttons (right)
      const RX = 1670, bh = UI.tap(96), b0 = 1080 - 30 - bh / 2 - (bh + 20);
      Zn.page(RX, 440, 440, 560, { seed: 419, rot: 0.012, tape: 1, dark: 0.35 });
      Zn.type(`${s} / ${of}`, RX, 270, 104, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 950 });
      Zn.stamp(this.passed ? 'PUBLISHED' : 'TRY AGAIN', RX, 440, { rot: -0.1, s: pop(T, this.endAt + 0.5, 0.3) * 0.62 });
      const msg = this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars to publish.`, ms = Math.round(UI.text(36));
      UI._wrap(msg, 380, `${ms}px ${HAND}`).forEach((l, j) => hand(l, RX, 580 + j * ms * 1.1, ms, this.endAt + 0.8, { color: Zn.C.red, seed: 980 + j, dur: 0.5 }));
      hand(`quiz code ${this.seed}`, RX, 690, 28, this.endAt + 0.6, { color: Zn.C.grey, seed: 985 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('nm-end' + i, b.label, RX, b0 + i * (bh + 20), { w: 440, h: 96, color: i === 0 ? Zn.C.red : BLUE, size: 36 })) b.go(); });
    }
  }

  /* an item's accepted readings that aren't among its options become chips too (seeded slot): picking them counts */
  function withAccept(it, seed, k) {
    const ids = it.options.map(o => o.id), extra = (it.accept || []).filter(a => !ids.includes(a) && Devices.DEVICES[a]);
    if (!extra.length) return it;
    const r = Bank.rng(String(seed) + ':' + k + ':accept');
    const opts = it.options.slice(), why = it.why.slice();
    extra.forEach(a => { const at = r.int(opts.length + 1); opts.splice(at, 0, { id: a, name: Devices.name(a), colour: Devices.colour(a) }); why.splice(at, 0, ''); });
    return Object.assign({}, it, { options: opts, why, answerIndex: opts.findIndex(o => o.id === it.device) });
  }
  const aOrAn = w => (/^[aeiou]/i.test(w) ? 'an ' : 'a ') + w;
  function inkTick(x, y, s, color, t0) {
    const pr = clamp((T - t0) / 0.3);
    if (pr <= 0) return;
    ink([[x - 18 * s, y], [x - 4 * s, y + 16 * s], [x + 24 * s, y - 22 * s]], { w: 6, color, seed: Math.round(x + y) % 97 + 1, prog: pr, passes: 1, step: 6 });
  }

  NameScene.STEP = STEP;
  NameScene.SPANS = SPANS;
  NameScene.layout = layout;
  NameScene.breakLines = breakLines;
  NameScene.sourceText = sourceText;
  Object.defineProperty(NameScene, 'solution', { get() { const s = Game.current(); return s instanceof NameScene ? s.solution : null; } });
  /* the live scene's tap rects: chips (device phase) or effect rows (effect phase), in logical px */
  NameScene.chips = () => {
    const s = Game.current(); if (!(s instanceof NameScene) || !s.item || UI.docked()) return [];
    const L = s.lay(false);
    if (s.phase === 'device') return L.chips.map(c => ({ i: c.i, id: c.id, x: c.x, y: c.y, w: c.w, h: c.h }));
    if (s.phase === 'effect') return L.rows.map(r => ({ i: r.i, x: r.x, y: r.y, w: r.w, h: r.h }));
    return [];
  };
  /* tests / screenshots: show this pool item as the current line (a fresh instance, seeded) */
  NameScene.preview = (id, seed = 1) => {
    const s = Game.current(); if (!(s instanceof NameScene) || !s.items) return false;
    const it = json().items.find(x => x.id === id); if (!it) return false;
    const r = Bank.rng(seed), inst = Devices.nameInstance(it, r, () => 0);
    s.items[s.line] = withAccept(inst, seed, s.line);
    s.setup(s.line); s.lineAt = Game.time - 5; s.lays.clear();
    return true;
  };
  Levels.name = NameScene;
})();
